import React, { useEffect, useState } from 'react';
import { DeveloperAccess } from '@/config/app';
import { SHIPMENT_WEBHOOK_EVENTS } from '@/services/api/webhooks';

// ─── Tiny presentational helpers (docs-only, no logic) ────────────────
const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code className="rounded bg-bg-weak-50 px-1 py-0.5 font-mono text-[12.5px] text-text-strong-950">
    {children}
  </code>
);

/** The host every example calls — sandbox and production share it. */
const BASE_URL = 'https://api.dashelectric.co';

/** Drops the `// note` annotations, so a copied example is valid JSON / shell. */
const stripNotes = (text: string) => text.replace(/ +\/\/ .*$/gm, '');

/**
 * A dark code panel with its label and a Copy button in the header bar (the
 * Sandbox's JsonBlock look). Copy leaves the `// notes` out. `wrap` breaks long
 * lines instead of scrolling — for error bodies, whose message is the point.
 */
const CodeBlock: React.FC<{
  children: string;
  label?: string;
  wrap?: boolean;
}> = ({ children, label, wrap = false }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(stripNotes(children));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (permissions / insecure context): nothing to do.
    }
  };
  return (
    <div className="overflow-hidden rounded-lg bg-bg-strong-950">
      <div className="flex items-center justify-between gap-3 border-b border-static-white/10 px-4 py-1.5">
        <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-static-white/60">
          {label}
        </span>
        <button
          type="button"
          onClick={copy}
          aria-label={`Copy ${label ?? 'example'}`}
          className="shrink-0 rounded px-1.5 py-1 text-xs font-medium text-static-white/70 hover:bg-static-white/10 hover:text-static-white focus-visible:outline-2 focus-visible:outline-static-white/60"
        >
          <i
            className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} mr-1`}
          />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre
        className={`px-4 py-3 font-mono text-[12px] leading-relaxed text-static-white ${
          wrap ? 'whitespace-pre-wrap break-words' : 'overflow-x-auto'
        }`}
      >
        {children}
      </pre>
    </div>
  );
};

/** A curl call against the shared host, one flag per line; a long URL gets a
 *  line of its own so it stays inside the panel. */
const curl = (
  method: 'GET' | 'POST',
  path: string,
  { key = '$DASH_API_KEY', body }: { key?: string; body?: string } = {}
) => {
  const verb = method === 'GET' ? 'curl' : `curl -X ${method}`;
  const url = `${BASE_URL}${path}`;
  return [
    ...(verb.length + url.length > 70
      ? [verb, `  ${url}`]
      : [`${verb} ${url}`]),
    `  -H "x-api-key: ${key}"`,
    ...(body ? ['  -H "Content-Type: application/json"', `  -d ${body}`] : []),
  ].join(' \\\n');
};

const Method: React.FC<{
  verb: 'GET' | 'POST';
  path: string;
  compact?: boolean;
}> = ({ verb, path, compact }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span
      className={`rounded px-2 py-0.5 font-mono text-[11px] font-bold ${
        verb === 'GET'
          ? 'bg-information-lighter text-information-dark'
          : 'bg-success-lighter text-success-dark'
      }`}
    >
      {verb}
    </span>
    <code
      className={`font-mono font-semibold text-text-strong-950 ${
        compact ? 'break-all text-xs' : 'text-[15px]'
      }`}
    >
      {path}
    </code>
  </div>
);

/** A plain 3-column table — kept for real tables (state moves, events). */
const FieldTable: React.FC<{
  rows: [string, string, string][]; // [field, required, notes]
  /** Header labels — request tables use the default; payload tables swap
   *  "Required" for "Type". */
  columns?: [string, string, string];
}> = ({ rows, columns = ['Field', 'Required', 'Notes'] }) => (
  <div className="overflow-x-auto">
    <table className="mt-2 w-full text-left text-[13px]">
      <thead>
        <tr className="border-b border-stroke-soft-200 text-text-sub-600">
          <th className="py-1.5 pr-4 font-medium">{columns[0]}</th>
          <th className="py-1.5 pr-4 font-medium">{columns[1]}</th>
          <th className="py-1.5 font-medium">{columns[2]}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-stroke-soft-200">
        {rows.map(([field, required, notes]) => (
          <tr key={field}>
            <td className="py-1.5 pr-4 align-top font-mono text-text-strong-950">
              {field}
            </td>
            <td className="py-1.5 pr-4 align-top text-text-sub-600">
              {required}
            </td>
            <td className="py-1.5 align-top text-text-sub-600">{notes}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** One field of a request body or response: name · type · Required · what. */
interface Param {
  name: string;
  type: string;
  /** true = "Required" badge; a string = a condition ("RETRIEVAL"). */
  required?: boolean | string;
  desc?: React.ReactNode;
  /** The fields of an object (or of each array entry), listed under it as
   *  `parent.child` with their own type. */
  children?: Param[];
}

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wide text-text-sub-600">
    {children}
  </h3>
);

/** One field row; an object's fields nest under it, indented on a rail. */
const ParamRow: React.FC<{ p: Param; parent?: string }> = ({ p, parent }) => {
  const name = parent ? `${parent}.${p.name}` : p.name;
  return (
    <li aria-label={name} className="py-2.5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <code className="font-mono text-[13px] font-semibold text-text-strong-950">
          {name}
        </code>
        <span className="font-mono text-xs text-text-sub-600">{p.type}</span>
        {p.required === true && (
          <span className="rounded bg-warning-lighter px-1.5 py-px text-[11px] font-semibold text-warning-dark">
            Required
          </span>
        )}
        {typeof p.required === 'string' && (
          <span className="rounded bg-bg-weak-50 px-1.5 py-px text-[11px] font-medium text-text-sub-600">
            {p.required}
          </span>
        )}
      </div>
      {p.desc && (
        <p className="mt-0.5 text-[13px] leading-relaxed text-text-sub-600">
          {p.desc}
        </p>
      )}
      {p.children && (
        <ul className="mt-2 divide-y divide-stroke-soft-200 border-l-2 border-stroke-soft-200 pl-3">
          {p.children.map((c) => (
            <ParamRow key={c.name} p={c} parent={name} />
          ))}
        </ul>
      )}
    </li>
  );
};

const ParamList: React.FC<{ title: string; params: Param[] }> = ({
  title,
  params,
}) => (
  <div role="group" aria-label={title}>
    <SubHeading>{title}</SubHeading>
    <ul className="mt-1.5 divide-y divide-stroke-soft-200 border-y border-stroke-soft-200">
      {params.map((p) => (
        <ParamRow key={p.name} p={p} />
      ))}
    </ul>
  </div>
);

// The create body's objects, field by field (types as the API validates them).
const CONTACT_FIELDS: Param[] = [
  { name: 'name', type: 'string', required: true },
  { name: 'phone', type: 'string', desc: 'e.g. +62811111111' },
];
const LOCATION_FIELDS = (point: 'pickup' | 'drop-off'): Param[] => [
  { name: 'address', type: 'string', required: true },
  {
    name: 'latitude',
    type: 'number',
    required: true,
    desc: 'Decimal degrees, e.g. -6.265523',
  },
  {
    name: 'longitude',
    type: 'number',
    required: true,
    desc: 'Decimal degrees, e.g. 106.818314',
  },
  {
    name: 'notes',
    type: 'string',
    desc: `For the rider at the ${point}, up to 1000 characters.`,
  },
];
const ITEM_FIELDS: Param[] = [
  {
    name: 'name',
    type: 'string',
    required: true,
    desc: 'PO / item identifier.',
  },
  { name: 'quantity', type: 'integer', required: true, desc: 'At least 1.' },
  { name: 'weight', type: 'number', required: true, desc: 'kg.' },
  { name: 'koli', type: 'integer', desc: 'Physical parcels. Default 1.' },
  { name: 'volume', type: 'number', desc: 'm³, 3 decimals. Default 0.' },
  { name: 'invoice', type: 'string', desc: 'Default = name.' },
  { name: 'description', type: 'string' },
  { name: 'price', type: 'number', desc: 'Item value.' },
];

// The response objects (detail, webhook): every field is always present,
// null when unknown.
const CONTACT_OUT_FIELDS: Param[] = [
  { name: 'name', type: 'string' },
  { name: 'phone', type: 'string | null' },
];
const LOCATION_OUT_FIELDS: Param[] = [
  { name: 'address', type: 'string' },
  { name: 'latitude', type: 'number' },
  { name: 'longitude', type: 'number' },
  { name: 'notes', type: 'string | null' },
];
const COURIER_FIELDS: Param[] = [
  { name: 'name', type: 'string' },
  { name: 'phone', type: 'string | null' },
  { name: 'uuid', type: 'string | null', desc: 'Stable courier id' },
  { name: 'code', type: 'string | null', desc: 'e.g. MTR0006903' },
  { name: 'profileImageUrl', type: 'string | null' },
];
const MEDIA_FIELDS: Param[] = [
  { name: 'id', type: 'uuid' },
  {
    name: 'type',
    type: 'enum',
    desc: 'PICKUP_PROOF | DELIVERY_PROOF | FAILED_PROOF | RETURN_PROOF | FAILED_RETURN_PROOF | SIGNATURE',
  },
  { name: 'url', type: 'string', desc: 'Public image URL' },
  { name: 'createdAt', type: 'ISO-8601 UTC' },
];
const REASON_FIELDS: Param[] = [
  { name: 'code', type: 'string | null', desc: 'e.g. EC5003' },
  { name: 'message', type: 'string | null' },
];

/** HTTP status as a small pill: 2xx green, 4xx amber, 5xx red. */
const HttpStatus: React.FC<{ code: number }> = ({ code }) => (
  <span
    className={`inline-flex min-w-9 justify-center rounded px-1.5 py-0.5 font-mono text-[11px] font-bold ${
      code >= 500
        ? 'bg-error-lighter text-error-dark'
        : code >= 400
          ? 'bg-warning-lighter text-warning-dark'
          : 'bg-success-lighter text-success-dark'
    }`}
  >
    {code}
  </span>
);

/** One error an endpoint can answer. `error` is the API's own message — the
 *  row opens to the exact body `{ status, error }` (no code: the HTTP status is
 *  the machine-readable part). */
interface ApiError {
  status: number;
  when: string;
  error: string;
}

const errorBody = (e: ApiError) => ({ status: 'Failed', error: e.error });

/**
 * Each error as a row — pill, code, cause — that opens to the body the API
 * sends, so the cause and its body are read together (the reference page's
 * error cards, folded to keep long lists scannable).
 */
const ErrorList: React.FC<{ errors: ApiError[]; title?: string }> = ({
  errors,
  title = 'Errors',
}) => (
  <div>
    <SubHeading>{title}</SubHeading>
    <ul className="mt-1.5 overflow-hidden rounded-lg border border-stroke-soft-200">
      {errors.map((e) => {
        const head = (
          <div className="grid grid-cols-[2.75rem_minmax(0,1fr)_1rem] items-start gap-x-3 px-3 py-2">
            <HttpStatus code={e.status} />
            <span className="min-w-0 text-[13px] leading-snug text-text-strong-950">
              {e.when}
            </span>
            <i
              aria-hidden="true"
              className="ri-arrow-down-s-line text-text-soft-400 transition-transform group-open:rotate-180"
            />
          </div>
        );
        return (
          <li
            key={`${e.status}-${e.when}`}
            aria-label={`${e.status} ${e.when}`}
            className="border-b border-stroke-soft-200 last:border-b-0"
          >
            <details className="group">
              <summary className="cursor-pointer list-none hover:bg-bg-weak-50 [&::-webkit-details-marker]:hidden">
                {head}
              </summary>
              <div className="px-3 pb-3">
                <CodeBlock label={`Error ${e.status}`} wrap>
                  {json(errorBody(e))}
                </CodeBlock>
              </div>
            </details>
          </li>
        );
      })}
    </ul>
  </div>
);

// ─── JSON examples (rendered with JSON.stringify: one key per line) ──
const json = (value: unknown) => JSON.stringify(value, null, 2);

/**
 * Appends `// note` to the first line starting with each prefix (after the
 * indent), or to every such line with `every` (a key that repeats, like each
 * `phone`), notes aligned in one column. The column is capped at `maxColumn`:
 * a longer line (a URL) gets its note right after it instead of pushing every
 * note off the panel. The JSON itself stays exactly as JSON.stringify formats it.
 */
const annotate = (
  text: string,
  notes: [string, string][],
  {
    every = false,
    maxColumn = 40,
  }: { every?: boolean; maxColumn?: number } = {}
) => {
  const lines = text.split('\n');
  const hits = notes.flatMap(([prefix, note]) => {
    const indexes = lines.flatMap((line, index) =>
      line.trimStart().startsWith(prefix) ? [index] : []
    );
    return (every ? indexes : indexes.slice(0, 1)).map((index) => ({
      index,
      note,
    }));
  });
  const fitting = hits
    .map(({ index }) => lines[index].length)
    .filter((length) => length + 2 <= maxColumn);
  const column = Math.max(0, ...fitting) + 2;
  for (const { index, note } of hits) {
    const line = lines[index];
    lines[index] =
      line.length + 2 <= column
        ? `${line.padEnd(column)}// ${note}`
        : `${line}  // ${note}`;
  }
  return lines.join('\n');
};

// Every field of the create body — a DOCKING DELIVERY (origin = the hub you
// drop at).
const CREATE_REQUEST = {
  bookingId: 'PO-2026-000123',
  type: 'DOCKING',
  handoverPoint: 'DELIVERY',
  serviceType: 'SAME_DAY',
  priorityTimeWindow: '08:00 - 10:00',
  sender: { name: 'PT Contoh Sejahtera', phone: '+62811111111' },
  recipient: { name: 'Apotek Fatih Farma', phone: '+62822222222' },
  origin: {
    address: 'Dash Hub Jakarta Selatan - Kemang, Jl. Kemang Selatan ID 2-8',
    latitude: -6.265523,
    longitude: 106.818314,
    notes: 'Drop di loket inbound',
  },
  destination: {
    address: 'Jl. Teluk Etna No. 63, Malang',
    latitude: -7.9666,
    longitude: 112.6326,
    notes: 'Titip ke resepsionis',
  },
  items: [
    {
      name: 'PO-26051802271820',
      quantity: 1,
      weight: 4.63,
      koli: 2,
      volume: 0.05,
      invoice: 'INV-2026-0518',
      description: 'Obat, jangan dibanting',
      price: 150000,
    },
  ],
};

const CREATE_REQUEST_NOTES: [string, string][] = [
  ['"serviceType"', 'optional, default SAME_DAY'],
  ['"priorityTimeWindow"', 'optional, "HH:MM - HH:MM"'],
  ['"phone"', 'optional'],
  ['"origin"', 'required for RETRIEVAL'],
  ['"notes"', 'optional, for the rider'],
  ['"koli"', 'optional, default 1'],
  ['"volume"', 'optional, m³'],
  ['"invoice"', 'optional, default = name'],
  ['"description"', 'optional'],
  ['"price"', 'optional'],
];

const CREATE_RESPONSE = {
  status: 'Success',
  data: {
    status: 'CREATED',
    shipmentId: '9c2f2c1e-5b7a-4e1d-9f10-3a1b2c3d4e5f',
    bookingId: 'PO-2026-000123',
    waybill: 'DLCTH260914AB12CD3',
    itemCount: 1,
  },
};

// Error bodies as the API answers them (checked on a running API, messages
// copied from nest-logistic-service).
const ERROR_EXAMPLE = {
  status: 'Failed',
  error: 'origin (pickup point) is required for RETRIEVAL shipments',
};
const SHIPMENT_ID = '9c2f2c1e-5b7a-4e1d-9f10-3a1b2c3d4e5f';
/** What the curl examples put in the path — set it once in the shell. */
const ID_VAR = '$SHIPMENT_ID';
const NOT_FOUND: ApiError = {
  status: 404,
  when: 'Unknown id, or a shipment of the other environment (live key ↔ sandbox shipment)',
  error: `Shipment ${SHIPMENT_ID} not found`,
};
const OTHER_CLIENT: ApiError = {
  status: 403,
  when: "Another company's shipment",
  error: 'Forbidden',
};
const scopeError = (scope: string): ApiError => ({
  status: 403,
  when: `The key lacks ${scope}`,
  error: 'Forbidden',
});

const COMMON_ERRORS: ApiError[] = [
  {
    status: 400,
    when: 'Body or query validation — error names a failing field',
    error: 'bookingId must be shorter than or equal to 100 characters',
  },
  {
    status: 400,
    when: 'Content-Type: application/json with an empty body — send {}',
    error:
      "Body cannot be empty when content-type is set to 'application/json'",
  },
  {
    status: 400,
    when: ':id is not a UUID',
    error: 'Validation failed (uuid is expected)',
  },
  {
    status: 401,
    when: 'Missing, unknown, revoked or expired API key',
    error: 'Unauthorized',
  },
  {
    status: 403,
    when: 'The key lacks the scope the route needs',
    error: 'Forbidden',
  },
  { ...NOT_FOUND, when: "Unknown id, or the other environment's shipment" },
  {
    status: 429,
    when: 'Rate limited — back off and retry',
    error: 'Too many requests, please try again after 15 minutes',
  },
  {
    status: 500,
    when: 'Unexpected failure — retry; create is idempotent by bookingId',
    error: 'Internal Server Error',
  },
];

const CREATE_ERRORS: ApiError[] = [
  {
    status: 400,
    when: 'DOCKING / DIRECT_2W without handoverPoint (or another value)',
    error:
      'handoverPoint must be one of the following values: DELIVERY, RETRIEVAL',
  },
  {
    status: 400,
    when: 'RETRIEVAL without origin',
    error: 'origin (pickup point) is required for RETRIEVAL shipments',
  },
  {
    status: 400,
    when: 'DELIVERY without destination',
    error: 'destination should not be null or undefined',
  },
  {
    status: 400,
    when: "The items' weights add up to more than 9,999,999.999 kg",
    error:
      "The items' weights add up to 12000000 kg; a shipment holds at most 9999999.999 kg",
  },
  {
    status: 400,
    when: "The items' volumes add up to more than 9,999,999.999 m³",
    error:
      "The items' volumes add up to 12000000 m³; a shipment holds at most 9999999.999 m³",
  },
  scopeError('shipments:write'),
  {
    status: 422,
    when: 'RETRIEVAL without destination, and no hub/pitstop could be picked',
    error:
      'destination is required: no Dash hub or client pitstop could be resolved for this RETRIEVAL shipment. Send destination or contact the Dash team.',
  },
];

const CANCEL_ERRORS: ApiError[] = [
  {
    status: 400,
    when: 'Not CREATED any more (scanned at a hub, or already cancelled)',
    error: 'Only CREATED shipments can be cancelled (current: AT_HUB)',
  },
  { ...NOT_FOUND, when: 'Unknown id, or the other environment' },
  OTHER_CLIENT,
  scopeError('shipments:cancel'),
];

// ── Outbound orders (warehouse) ─────────────────────────────────────────
const ORDER_ID = '4afd5c18-e2d9-4ff2-82ac-d083a4fe1877';
const ORDER_ID_VAR = '$ORDER_ID';

const ORDER_REQUEST = {
  externalID: 'SO-2026-001234',
  expectedDeliveryDate: '2026-10-25',
  recipient: { name: 'Budi Santoso', phone: '081234567890' },
  destination: {
    address: 'Jl. Kemang Raya 1, Jakarta Selatan',
    latitude: -6.26,
    longitude: 106.81,
    notes: 'Pagar hijau',
  },
  lines: [
    { sku: '100001', qty: 2 },
    { sku: '100002', qty: 1 },
  ],
  notes: 'Kirim pagi',
};

const ORDER_REQUEST_NOTES: [string, string][] = [
  ['"externalID"', 'your order id — the idempotency key'],
  ['"expectedDeliveryDate"', 'not before today (WIB)'],
  ['"latitude"', 'required — no geocoding'],
  ['"sku"', 'from your SKU catalog at Dash'],
];

const ORDER = {
  id: ORDER_ID,
  code: 'OUT-ATLAS-102326-Ab3K',
  externalID: 'SO-2026-001234',
  status: 'OPEN',
  hub: { id: 906, name: 'Dash Hub Kemang', code: 'DASH_KMG' },
  orderDate: '2026-10-23',
  expectedDeliveryDate: '2026-10-25',
  recipient: { name: 'Budi Santoso', phone: '081234567890' },
  destination: {
    address: 'Jl. Kemang Raya 1, Jakarta Selatan',
    latitude: -6.26,
    longitude: 106.81,
    notes: 'Pagar hijau',
    cityID: 2,
    city: 'Kota Administrasi Jakarta Selatan',
  },
  lines: [
    { sku: '100001', itemName: 'Atlas Headstrap', uomName: 'pcs', qty: 2 },
    { sku: '100002', itemName: 'Atlas Mask', uomName: 'pcs', qty: 1 },
  ],
  notes: 'Kirim pagi',
  fulfillment: null,
  cancellation: null,
  createdAt: '2026-10-23T03:15:02.000Z',
  updatedAt: '2026-10-23T03:15:02.000Z',
};

const ORDER_CREATE_RESPONSE = {
  status: 'Success',
  message: 'Outbound order created',
  data: { result: 'CREATED', order: ORDER },
};

const ORDER_NOT_FOUND: ApiError = {
  status: 404,
  when: 'Unknown id, an order of the other environment, or not a client order',
  error: `Outbound order ${ORDER_ID} not found`,
};
const OTHER_CLIENT_ORDER: ApiError = {
  status: 403,
  when: "Another company's order",
  error: 'Forbidden',
};

const ORDER_CREATE_ERRORS: ApiError[] = [
  {
    status: 400,
    when: 'Field problems — every one in one message, `field: message; …`',
    error:
      'externalID: externalID is required.; lines[0].sku: SKU 100009 is not in your catalog.',
  },
  {
    status: 422,
    when: 'No hub holds enough of a SKU',
    error:
      'Not enough stock in any single hub for this order: SKU 100001 needs 12, at most 5 available.',
  },
  {
    status: 422,
    when: 'Every SKU is in stock, but in different hubs (one order ships from one hub)',
    error:
      'No single hub holds every SKU of this order (100001 ×2, 100002 ×1) — they are in stock in different hubs, and one order ships from one hub.',
  },
  {
    status: 422,
    when: 'Sandbox key: more than 100 of a SKU',
    error:
      'Not enough stock in Sandbox (every SKU holds 100) for this order: SKU 100001 needs 120, at most 100 available.',
  },
  scopeError('shipments:write'),
];

const ORDER_CANCEL_ERRORS: ApiError[] = [
  {
    status: 409,
    when: 'Shipping already submitted (or already cancelled)',
    error:
      'The order is SHIPPED — an order can only be cancelled before it ships. To stop a shipped order, cancel its shipment: POST /v1/integration/shipments/:id/cancel.',
  },
  ORDER_NOT_FOUND,
  OTHER_CLIENT_ORDER,
  scopeError('shipments:cancel'),
];

// ── Shared sections (Authentication / Errors) ───────────────────────────
// They speak the client's own API: shipment examples when it has the
// shipment API, outbound-order examples for a WMS-only client.
const ORDER_ERROR_EXAMPLE = {
  status: 'Failed',
  error: 'externalID: externalID is required.',
};

const ORDER_COMMON_ERRORS: ApiError[] = [
  {
    status: 400,
    when: 'Body or query validation — error names a failing field',
    error: 'lines[0].qty: Qty must be greater than 0.',
  },
  {
    status: 400,
    when: 'Content-Type: application/json with an empty body — send {}',
    error:
      "Body cannot be empty when content-type is set to 'application/json'",
  },
  {
    status: 400,
    when: ':id is not a UUID',
    error: 'Validation failed (uuid is expected)',
  },
  {
    status: 401,
    when: 'Missing, unknown, revoked or expired API key',
    error: 'Unauthorized',
  },
  {
    status: 403,
    when: 'The key lacks the scope the route needs',
    error: 'Forbidden',
  },
  { ...ORDER_NOT_FOUND, when: "Unknown id, or the other environment's order" },
  {
    status: 429,
    when: 'Rate limited — back off and retry',
    error: 'Too many requests, please try again after 15 minutes',
  },
  {
    status: 500,
    when: 'Unexpected failure — retry; create is idempotent by externalID',
    error: 'Internal Server Error',
  },
];

const SHARED_EXAMPLES = {
  shipments: {
    requestPath: `/v1/integration/shipments/${ID_VAR}`,
    error: ERROR_EXAMPLE,
    errors: COMMON_ERRORS,
  },
  orders: {
    requestPath: `/v1/integration/outbound-orders/${ORDER_ID_VAR}`,
    error: ORDER_ERROR_EXAMPLE,
    errors: ORDER_COMMON_ERRORS,
  },
};

/** Shipment examples for a client with the shipment API, else (WMS only)
 *  the outbound-order ones. */
const sharedExamplesOf = (access: DeveloperAccess) =>
  access.shipments ? SHARED_EXAMPLES.shipments : SHARED_EXAMPLES.orders;

const WEBHOOK_URL = 'https://your-system.example/webhooks/dash';
const MEDIA_BASE = 'https://storage.googleapis.com/<bucket>';
const courierPhoto = (code: string) =>
  `${MEDIA_BASE}/driver-media/${code}/selfie.jpg`;

// GET /v1/integration/shipments/:id — webhook-shaped objects (courier, event,
// media), richer content: full history, every photo, item details.
const POD_PHOTO = {
  id: '49b727e9-8f24-4957-a552-8d0ed36f0928',
  type: 'DELIVERY_PROOF',
  url: `${MEDIA_BASE}/delivery-proof/DLCTH260914AB12CD3/pod-1.jpg`,
  createdAt: '2026-09-14T04:14:51.000Z',
};

const DETAIL_RESPONSE = {
  status: 'Success',
  data: {
    id: '9c2f2c1e-5b7a-4e1d-9f10-3a1b2c3d4e5f',
    waybill: 'DLCTH260914AB12CD3',
    bookingId: 'PO-2026-000123',
    environment: 'LIVE',
    client: { id: 5, name: 'PT Contoh Sejahtera' },
    status: 'COMPLETED',
    hasReturn: false,
    type: 'DOCKING',
    serviceType: 'SAME_DAY',
    priorityTimeWindow: '08:00 - 10:00',
    courier: {
      name: 'Babe Santoso',
      phone: '6281234567890',
      uuid: '6f3c2a1e-8b7d-4c2a-9e1f-1a2b3c4d5e01',
      code: 'MTR0006903',
      profileImageUrl: courierPhoto('MTR0006903'),
    },
    sender: { name: 'PT Contoh Sejahtera', phone: '+62811111111' },
    recipient: { name: 'Apotek Fatih Farma', phone: '+62822222222' },
    origin: null,
    destination: {
      address: 'Jl. Teluk Etna No. 63, Malang',
      latitude: -7.9666,
      longitude: 112.6326,
      notes: 'Titip ke resepsionis',
    },
    totalWeight: 4.63,
    totalVolume: 0.05,
    itemCount: 1,
    totalKoli: 2,
    cancellationReason: null,
    createdAt: '2026-09-14T01:02:03.000Z',
    dispatchedAt: '2026-09-14T02:10:00.000Z',
    cancelledAt: null,
    updatedAt: '2026-09-14T04:15:09.000Z',
    items: [
      {
        id: 'd1e2f3a4-5b6c-4d7e-8f90-1a2b3c4d5e6f',
        name: 'PO-26051802271820',
        invoice: 'INV-2026-0518',
        description: 'Obat, jangan dibanting',
        quantity: 1,
        koli: 2,
        weight: 4.63,
        volume: 0.05,
        status: 'COMPLETED',
        return: null,
      },
    ],
    media: [POD_PHOTO],
    timeline: [
      {
        id: '7c1d2e3f-4a5b-4c6d-8e7f-9a0b1c2d3e4f',
        type: 'STATUS_CHANGED',
        status: 'COMPLETED',
        message: 'Pengantaran selesai',
        hubName: null,
        reason: null,
        media: [POD_PHOTO],
        createdBy: 'MTR0006903 - Babe Santoso',
        occurredAt: '2026-09-14T04:15:09.000Z',
      },
      {
        id: '5e6f7a8b-9c0d-4e1f-a2b3-c4d5e6f7a8b9',
        type: 'STATUS_CHANGED',
        status: 'AT_HUB',
        message:
          'Shipment sudah tiba di gudang Hub Malang dan sudah di-scan oleh PIC gudang',
        hubName: 'Hub Malang',
        reason: null,
        media: [],
        createdBy: 'pic.malang@dashelectric.co',
        occurredAt: '2026-09-14T01:40:12.000Z',
      },
    ],
  },
};

const REGISTER_REQUEST = { url: WEBHOOK_URL, environment: 'TEST' };

const REGISTER_RESPONSE = {
  status: 'Success',
  message: 'Store this signing secret now — it will never be shown again',
  data: {
    id: '3f9a1c2e-9d4b-4e7a-b1c0-5a6d7e8f9a0b',
    url: WEBHOOK_URL,
    environment: 'TEST',
    createdAt: '2026-09-14T01:00:00.000Z',
    secret: 'dash_whsec_…',
  },
};

const ITEM_ID = 'd1e2f3a4-5b6c-4d7e-8f90-a1b2c3d4e5f6';

/**
 * A full v3 delivery: the COMPLETED of a one-item DOCKING shipment, in the
 * same envelope as Dash's API responses.
 */
const COMPLETED_EVENT = {
  status: 'Success',
  message: 'OK',
  data: {
    event: {
      id: '3f8a1c2e-9d4b-4e7a-b1c0-5a6d7e8f9a0b',
      type: 'STATUS_CHANGED',
      sequence: 5,
      status: 'COMPLETED',
      message: 'Pengantaran selesai',
      hubName: null,
      occurredAt: '2026-09-14T04:15:09.000Z',
      reason: null,
      media: [
        {
          id: '49b727e9-8f24-4957-a552-8d0ed36f0928',
          type: 'DELIVERY_PROOF',
          url: `${MEDIA_BASE}/delivery-proof/DLCTH260914AB12CD3/pod-1.jpg`,
          createdAt: '2026-09-14T04:14:51.000Z',
        },
      ],
    },
    shipment: {
      id: '9c2f2c1e-5b7a-4e1d-9f10-3a1b2c3d4e5f',
      waybill: 'DLCTH260914AB12CD3',
      bookingId: 'PO-2026-000123',
      environment: 'LIVE',
      status: 'COMPLETED',
      courier: {
        name: 'Babe Santoso',
        phone: '6281234567890',
        uuid: '6f3c2a1e-8b7d-4c2a-9e1f-1a2b3c4d5e01',
        code: 'MTR0006903',
        profileImageUrl: courierPhoto('MTR0006903'),
      },
      serviceType: 'SAME_DAY',
      totalWeight: 4.63,
      sender: { name: 'PT Contoh Sejahtera', phone: '+62811111111' },
      recipient: { name: 'Apotek Fatih Farma', phone: '+62822222222' },
      origin: null,
      destination: {
        address: 'Jl. Teluk Etna No. 63, Malang',
        latitude: -7.9666,
        longitude: 112.6326,
      },
      items: [
        {
          id: ITEM_ID,
          name: 'PO-26051802271820',
          weight: 4.63,
          status: 'COMPLETED',
          return: null,
        },
      ],
      createdAt: '2026-09-14T01:02:03.000Z',
      dispatchedAt: '2026-09-14T02:30:12.000Z',
    },
  },
};

/** AT_HUB: only the event fields that differ — it names the hub. */
const AT_HUB_EVENT = {
  data: {
    event: {
      id: '5d0f3b8e-1c2a-4e6f-9b7d-2a4c6e8f0b1d',
      type: 'STATUS_CHANGED',
      sequence: 2,
      status: 'AT_HUB',
      message:
        'Shipment sudah tiba di gudang DASH-DAAN-MOGOT dan sudah di-scan oleh PIC gudang',
      hubName: 'DASH-DAAN-MOGOT',
    },
  },
};

/** FAILED: only the fields that differ from the COMPLETED above. */
const FAILED_EVENT = {
  data: {
    event: {
      id: '7b1d0c55-2a4e-4f8b-9c3d-6e5f4a3b2c1d',
      type: 'STATUS_CHANGED',
      sequence: 4,
      status: 'FAILED',
      message: 'Penerima tidak ada di alamat',
      occurredAt: '2026-09-14T03:52:40.000Z',
      reason: { code: 'EC5003', message: 'Penerima tidak ada di alamat' },
      media: [
        {
          id: '86cb85bb-ed47-4300-8370-70d9c874ed23',
          type: 'FAILED_PROOF',
          url: `${MEDIA_BASE}/failed-proof/DLCTH260914AB12CD3/failed-1.jpg`,
          createdAt: '2026-09-14T03:52:02.000Z',
        },
      ],
    },
    shipment: {
      status: 'FAILED',
      items: [
        {
          id: ITEM_ID,
          name: 'PO-26051802271820',
          status: 'FAILED',
          return: null,
        },
      ],
    },
  },
};

// ─── Integration flow (visual stepper at the top of the tab) ──────────
interface FlowStepMethod {
  verb: 'GET' | 'POST';
  path: string;
  label?: string;
}

interface FlowStep {
  icon: string;
  title: string;
  /** Alternatives, rendered with an "or" divider between them. */
  methods: FlowStepMethod[];
  note: React.ReactNode;
}

const FLOW_STEPS: FlowStep[] = [
  {
    icon: 'ri-add-box-line',
    title: 'Create',
    methods: [{ verb: 'POST', path: '/v1/integration/shipments' }],
    note: (
      <>
        Store <Code>shipmentId</Code> + <Code>waybill</Code>. Timed out? Retry
        the same payload — idempotent by <Code>bookingId</Code>.
      </>
    ),
  },
  {
    icon: 'ri-radar-line',
    title: 'Track or webhook',
    methods: [
      {
        verb: 'GET',
        path: '/v1/integration/shipments/:id',
        label: 'Poll',
      },
      {
        verb: 'POST',
        path: '/v1/integration/webhook-endpoints',
        label: 'Push',
      },
    ],
    note: (
      <>
        Poll the detail endpoint for status, items, timeline &amp; POD — or
        register a webhook endpoint once and get every shipment event pushed,
        each with the full shipment.
      </>
    ),
  },
  {
    icon: 'ri-close-circle-line',
    title: 'Cancel',
    methods: [{ verb: 'POST', path: '/v1/integration/shipments/:id/cancel' }],
    note: (
      <>
        Optional — only while the shipment is still <Code>CREATED</Code>
        (before the first hub scan).
      </>
    ),
  },
  {
    icon: 'ri-map-pin-user-line',
    title: 'Share tracking',
    methods: [{ verb: 'GET', path: '/v1/public/track/:waybill' }],
    note: <>Give end customers the public tracking page — no auth needed.</>,
  },
];

/**
 * The recommended integration flow as a stepper: horizontal on desktop,
 * stacked on mobile. Steps are informational, not interactive — connectors
 * are decorative (aria-hidden), the ordered list carries the semantics.
 */
const FlowSection: React.FC = () => (
  <section
    id="integration-flow"
    aria-labelledby="integration-flow-title"
    className="scroll-mt-16 rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-6"
  >
    <h2
      id="integration-flow-title"
      className="text-xl font-semibold tracking-tight text-text-strong-950"
    >
      Integration flow
    </h2>
    <p className="mt-1 text-sm text-text-sub-600">
      The whole integration is four calls — details for each are in the sections
      below.
    </p>
    <ol className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-stretch lg:gap-2">
      {FLOW_STEPS.map((step, i) => (
        <React.Fragment key={step.title}>
          {i > 0 && (
            <li
              aria-hidden="true"
              className="flex items-center justify-center text-text-soft-400"
            >
              <i className="ri-arrow-down-line text-lg lg:hidden" />
              <i className="ri-arrow-right-line hidden text-lg lg:block" />
            </li>
          )}
          <li className="flex-1 rounded-xl border border-stroke-soft-200 bg-bg-weak-50 p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary font-mono text-[11px] font-bold text-static-white">
                {i + 1}
              </span>
              <i className={`${step.icon} text-base text-text-sub-600`} />
              <span className="text-sm font-semibold text-text-strong-950">
                {step.title}
              </span>
            </div>
            <div className="mt-2.5 space-y-1.5">
              {step.methods.map((m, mi) => (
                <React.Fragment key={m.path}>
                  {mi > 0 && (
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-text-soft-400">
                      or
                    </div>
                  )}
                  <div className="flex items-start gap-1.5">
                    {m.label && (
                      <span className="mt-px text-[11px] font-medium text-text-sub-600">
                        {m.label}:
                      </span>
                    )}
                    <Method verb={m.verb} path={m.path} compact />
                  </div>
                </React.Fragment>
              ))}
            </div>
            <p className="mt-2 text-xs leading-relaxed text-text-sub-600">
              {step.note}
            </p>
          </li>
        </React.Fragment>
      ))}
    </ol>
  </section>
);

/**
 * One endpoint section, Stripe-style: prose + params on the left, the
 * request/response examples on the right from xl up (stacked below, where two
 * columns would squeeze both). The code track is the wider one (5/7) so
 * annotated examples fit; `min-w-0` on both tracks
 * keeps <pre> overflow-x scrolling inside its column. `scroll-mt` leaves room
 * for the sticky section nav when jumping to the anchor.
 */
const Section: React.FC<{
  id: string;
  icon: string;
  title: string;
  children: React.ReactNode;
  code?: React.ReactNode;
  /** Not for this client (per-client API access) — render nothing. */
  hidden?: boolean;
}> = ({ id, icon, title, children, code, hidden }) =>
  hidden ? null : (
    <section
      id={id}
      aria-labelledby={`${id}-title`}
      className="scroll-mt-16 rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-6"
    >
      <div className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-(--primary-alpha-10) text-primary">
          <i className={`${icon} text-base`} />
        </span>
        <h2
          id={`${id}-title`}
          className="text-xl font-semibold tracking-tight text-text-strong-950"
        >
          {title}
        </h2>
      </div>
      <div
        className={`mt-5 grid gap-6 ${
          code ? 'xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]' : ''
        }`}
      >
        <div className="min-w-0 space-y-5 text-[15px] leading-relaxed text-text-sub-600">
          {children}
        </div>
        {code && <div className="min-w-0 space-y-4">{code}</div>}
      </div>
    </section>
  );

/** The page's sections, in order — the sticky nav and its scroll-spy. */
/** `access`: which API shows the section (DeveloperAccess, from the
 *  backend's sandbox-availability); none = shown to everyone on the page. */
interface NavItem {
  id: string;
  label: string;
  group: number;
  access?: keyof DeveloperAccess;
}

const NAV: NavItem[] = [
  { id: 'integration-flow', label: 'Overview', group: 0, access: 'shipments' },
  { id: 'authentication', label: 'Authentication', group: 0 },
  { id: 'errors', label: 'Errors', group: 0 },
  { id: 'create-shipment', label: 'Create', group: 1, access: 'shipments' },
  { id: 'get-shipment', label: 'Get detail', group: 1, access: 'shipments' },
  { id: 'cancel-shipment', label: 'Cancel', group: 1, access: 'shipments' },
  {
    id: 'create-outbound-order',
    label: 'Outbound order',
    group: 2,
    access: 'orders',
  },
  {
    id: 'track-outbound-order',
    label: 'Get & list',
    group: 2,
    access: 'orders',
  },
  {
    id: 'cancel-outbound-order',
    label: 'Cancel order',
    group: 2,
    access: 'orders',
  },
  { id: 'get-stock', label: 'Stock', group: 2, access: 'orders' },
  { id: 'webhooks', label: 'Webhooks', group: 3, access: 'shipments' },
];

/** How far below the top of the viewport a section counts as "in view" —
 *  just under the sticky nav. */
const ACTIVE_LINE = 120;

const jumpTo = (id: string, behavior: ScrollBehavior = 'smooth') => {
  document.getElementById(id)?.scrollIntoView?.({ behavior, block: 'start' });
};

/**
 * "On this page": a sticky bar of section links (the app already has a left
 * sidebar, so the reference page's left nav becomes a top bar here). It marks
 * the section in view as you scroll, and each link is shareable — `?tab=docs#webhooks`
 * opens the tab on that section.
 */
const SectionNav: React.FC<{ items: NavItem[] }> = ({ items }) => {
  const [active, setActive] = useState(items[0].id);

  useEffect(() => {
    // The section in view = the last one whose top has passed under the bar.
    // Read from the layout on scroll (the docs scroll inside the app's main
    // pane, so listen in the capture phase); a rAF keeps it to one read a frame.
    let frame = 0;
    const pick = () => {
      frame = 0;
      let current = items[0].id;
      for (const { id } of items) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= ACTIVE_LINE) current = id;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(pick);
    };
    // A shared link (?tab=docs#webhooks) and back / forward land on a section.
    const fromHash = (behavior: ScrollBehavior) => {
      const hash = window.location.hash.slice(1);
      if (items.some((n) => n.id === hash)) {
        setActive(hash);
        jumpTo(hash, behavior);
      }
    };
    const onHashChange = () => fromHash('smooth');
    fromHash('auto');
    document.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    });
    window.addEventListener('hashchange', onHashChange);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('hashchange', onHashChange);
    };
  }, [items]);

  return (
    <nav
      aria-label="On this page"
      className="sticky top-0 z-20 -mx-1 border-b border-stroke-soft-200 bg-bg-weak-50/95 px-1 py-2 backdrop-blur"
    >
      <ul className="flex items-center gap-1 overflow-x-auto">
        {items.map((n, i) => (
          <React.Fragment key={n.id}>
            {i > 0 && items[i - 1].group !== n.group && (
              <li
                aria-hidden="true"
                className="mx-1 h-4 w-px shrink-0 bg-stroke-sub-300"
              />
            )}
            <li className="shrink-0">
              <a
                href={`#${n.id}`}
                aria-current={active === n.id ? 'location' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  setActive(n.id);
                  jumpTo(n.id);
                  window.history.replaceState(
                    null,
                    '',
                    `${window.location.pathname}${window.location.search}#${n.id}`
                  );
                }}
                className={`block rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${
                  active === n.id
                    ? 'bg-(--primary-alpha-10) text-primary'
                    : 'text-text-sub-600 hover:bg-bg-white-0 hover:text-text-strong-950'
                }`}
              >
                {n.label}
              </a>
            </li>
          </React.Fragment>
        ))}
      </ul>
    </nav>
  );
};

/**
 * In-app condensed rendering of the partner API reference
 * (nest-logistic-service docs/shipment/SHIPMENT-INTEGRATION-ENDPOINTS.md; the
 * webhook payload is docs/api-integration/webhook-payload-v2.md).
 * Keep this in sync with those docs — they are the contract, this is the
 * summary.
 */
/** Every API shown — the page before per-client gating (and its tests). */
const ALL_APIS: DeveloperAccess = { shipments: true, orders: true };

const ApiDocsTab: React.FC<{ access?: DeveloperAccess }> = ({
  access = ALL_APIS,
}) => (
  <div className="space-y-6">
    <SectionNav items={NAV.filter((n) => !n.access || access[n.access])} />
    {access.shipments && <FlowSection />}

    <Section
      id="authentication"
      icon="ri-key-2-line"
      title="Authentication"
      code={
        <>
          <CodeBlock label="Base URL">{BASE_URL}</CodeBlock>
          <CodeBlock label="Example request">
            {curl('GET', sharedExamplesOf(access).requestPath)}
          </CodeBlock>
        </>
      }
    >
      <p>
        Every request goes to <Code>{BASE_URL}</Code> with your API key in the{' '}
        <Code>x-api-key</Code> header. The key identifies your company — there
        is no client id anywhere in a payload. Keys carry scopes (
        <Code>shipments:read</Code> / <Code>shipments:write</Code> /{' '}
        <Code>shipments:cancel</Code>, or <Code>all</Code> for full access);
        calling a route without the matching scope returns <Code>403</Code>.
        {!access.shipments && (
          <>
            {' '}
            Outbound orders use the same scopes: <Code>shipments:read</Code> to
            get and list, <Code>shipments:write</Code> to create,{' '}
            <Code>shipments:cancel</Code> to cancel.
          </>
        )}
      </p>
      <p>
        <b className="text-text-strong-950">Environments:</b> a{' '}
        <Code>dash_test_…</Code> key only authenticates sandbox requests, a{' '}
        <Code>dash_live_…</Code> key only production — same URL, same payload.
        Going live is a key swap, nothing else. All rejections are a uniform{' '}
        <Code>401</Code>.
      </p>
      <p>
        Every response uses the envelope{' '}
        <Code>{'{ "status": "Success", "message?", "data" }'}</Code>; errors use{' '}
        <Code>{'{ "status": "Failed", "error" }'}</Code> — see{' '}
        <a
          href="#errors"
          onClick={(e) => {
            e.preventDefault();
            jumpTo('errors');
          }}
          className="font-medium text-primary hover:underline"
        >
          Errors
        </a>
        .
      </p>
    </Section>

    <Section
      id="errors"
      icon="ri-error-warning-line"
      title="Errors"
      code={
        <>
          <CodeBlock label="Error envelope" wrap>
            {json(sharedExamplesOf(access).error)}
          </CodeBlock>
        </>
      }
    >
      <p>
        Every failure answers{' '}
        <Code>{'{ "status": "Failed", "error": "…" }'}</Code> with a standard
        HTTP status. <Code>error</Code> is a human-readable message for your
        logs.{' '}
        <b className="text-text-strong-950">
          Branch on the HTTP status, never on the <Code>error</Code> text
        </b>{' '}
        — its wording can change. Open a row to see its body. Each endpoint
        below lists its own errors; these apply to every endpoint:
      </p>
      <ErrorList
        title="Every endpoint"
        errors={sharedExamplesOf(access).errors}
      />
    </Section>

    <Section
      hidden={!access.shipments}
      id="create-shipment"
      icon="ri-add-box-line"
      title="Create shipment"
      code={
        <>
          <CodeBlock label="Request">
            {curl('POST', '/v1/integration/shipments', {
              body: '@shipment.json',
            })}
          </CodeBlock>
          <CodeBlock label="Body — shipment.json">
            {annotate(json(CREATE_REQUEST), CREATE_REQUEST_NOTES, {
              every: true,
            })}
          </CodeBlock>
          <CodeBlock label="Response 200">
            {annotate(json(CREATE_RESPONSE), [
              ['"status": "CREATED"', '"EXISTS" on an idempotent replay'],
            ])}
          </CodeBlock>
        </>
      }
    >
      <Method verb="POST" path="/v1/integration/shipments" />
      <p>
        One request = one shipment (AWB); each entry in <Code>items</Code>{' '}
        becomes a tracked item.{' '}
        <b className="text-text-strong-950">
          Idempotent by <Code>bookingId</Code>
        </b>{' '}
        — replaying the same booking returns <Code>{'"status": "EXISTS"'}</Code>{' '}
        instead of duplicating, so retrying on timeout is always safe.
      </p>
      <p>
        <Code>type</Code> declares how the goods are carried:{' '}
        <Code>DOCKING</Code> (through a Dash hub) or <Code>DIRECT_2W</Code>{' '}
        (motorcycle, through one of your own pitstops).
      </p>
      <p>
        <Code>handoverPoint</Code> (required) says how the goods meet that
        hub/pitstop: <Code>DELIVERY</Code> — they leave it for the recipient (
        <Code>origin</Code> optional); <Code>RETRIEVAL</Code> — Dash collects
        them at <Code>origin</Code> (required) and brings them to it (
        <Code>destination</Code> optional). Dash picks the nearest hub/pitstop
        for you and the shipment goes to <Code>AT_HUB</Code> right away
        (&quot;Shipment siap ditangani&quot;) — except a <Code>DOCKING</Code>{' '}
        <Code>DELIVERY</Code>, which waits for the hub&apos;s inbound scan.
      </p>
      <ParamList
        title="Request body"
        params={[
          {
            name: 'bookingId',
            type: 'string',
            required: true,
            desc: 'Your order id — the idempotency key.',
          },
          {
            name: 'type',
            type: 'string',
            required: true,
            desc: 'DOCKING | DIRECT_2W',
          },
          {
            name: 'handoverPoint',
            type: 'string',
            required: true,
            desc: 'DELIVERY | RETRIEVAL.',
          },
          {
            name: 'serviceType',
            type: 'string',
            desc: 'SLA tag. Default SAME_DAY.',
          },
          {
            name: 'priorityTimeWindow',
            type: 'string',
            desc: 'Pickup window, "HH:MM - HH:MM" (24h).',
          },
          {
            name: 'sender',
            type: 'object',
            required: true,
            children: CONTACT_FIELDS,
          },
          {
            name: 'recipient',
            type: 'object',
            required: true,
            children: CONTACT_FIELDS,
          },
          {
            name: 'origin',
            type: 'object',
            required: 'RETRIEVAL',
            desc: 'The pickup point. Optional for DELIVERY (the hub/pitstop fills it).',
            children: LOCATION_FIELDS('pickup'),
          },
          {
            name: 'destination',
            type: 'object',
            required: 'DELIVERY',
            desc: 'The drop-off point. Optional for RETRIEVAL (the hub/pitstop fills it).',
            children: LOCATION_FIELDS('drop-off'),
          },
          {
            name: 'items[]',
            type: 'array',
            required: true,
            desc: 'At least one.',
            children: ITEM_FIELDS,
          },
        ]}
      />
      <p className="text-[13px]">
        Optional text fields are trimmed; blank (<Code>""</Code>) counts as not
        sent.
      </p>
      <ErrorList errors={CREATE_ERRORS} />
    </Section>

    <Section
      hidden={!access.shipments}
      id="get-shipment"
      icon="ri-radar-line"
      title="Get shipment detail"
      code={
        <>
          <CodeBlock label="Request">
            {curl('GET', `/v1/integration/shipments/${ID_VAR}`)}
          </CodeBlock>
          <CodeBlock label="Response 200">
            {annotate(json(DETAIL_RESPONSE), [
              ['"environment"', 'LIVE | TEST (sandbox)'],
              ['"courier": {', 'null until assigned'],
              ['"origin": null', 'drop-off: set at hub scan'],
              ['"items": [', 'as sent, + status, return'],
              ['"media": [', 'every proof photo, once each'],
              ['"timeline": [', 'webhook events, newest first'],
              ['"createdBy": "MTR', 'the rider'],
              ['"createdBy": "pic', 'the ops who scanned'],
            ])}
          </CodeBlock>
        </>
      }
    >
      <Method verb="GET" path="/v1/integration/shipments/:id" />
      <p>
        <Code>:id</Code> is the <Code>shipmentId</Code> from create. The objects
        match the webhook — <Code>courier</Code>, <Code>sender</Code> /{' '}
        <Code>recipient</Code>, <Code>origin</Code> / <Code>destination</Code>,
        each <Code>timeline[]</Code> entry is an <Code>event</Code> and every
        photo an <Code>event.media</Code> object — but the detail carries more:
        the whole history, every proof photo in <Code>media[]</Code>, item
        details (invoice, description, quantity, koli, volume). What you sent at
        create reads back (<Code>null</Code> when not sent). Each timeline entry
        also says who wrote it (<Code>createdBy</Code>): the Dash ops who
        scanned or dispatched, the rider as <Code>{'"{code} - {name}"'}</Code>{' '}
        on a delivery event, your user or company on a cancel, else{' '}
        <Code>system</Code>. Each row of{' '}
        <Code>GET /v1/integration/shipments</Code> is this head, without{' '}
        <Code>items</Code> / <Code>media</Code> / <Code>timeline</Code>.
      </p>
      <ParamList
        title="Response fields"
        params={[
          {
            name: 'environment',
            type: 'LIVE | TEST',
            desc: 'TEST for a sandbox shipment (dash_test_ key), LIVE otherwise — the same as the webhook’s shipment.environment',
          },
          {
            name: 'client',
            type: 'object',
            desc: 'Your company',
            children: [
              { name: 'id', type: 'integer' },
              { name: 'name', type: 'string' },
            ],
          },
          {
            name: 'courier',
            type: 'object | null',
            desc: 'As in the webhook; null until a courier is assigned',
            children: COURIER_FIELDS,
          },
          {
            name: 'sender',
            type: 'object',
            desc: 'As sent at create',
            children: CONTACT_OUT_FIELDS,
          },
          {
            name: 'recipient',
            type: 'object',
            desc: 'As sent at create',
            children: CONTACT_OUT_FIELDS,
          },
          {
            name: 'origin',
            type: 'object | null',
            desc: 'Null for a DOCKING + DELIVERY sent without origin, until the hub scan',
            children: LOCATION_OUT_FIELDS,
          },
          {
            name: 'destination',
            type: 'object',
            children: LOCATION_OUT_FIELDS,
          },
          {
            name: 'priorityTimeWindow',
            type: 'string | null',
            desc: 'As sent at create; null when not sent',
          },
          {
            name: 'totalWeight / totalVolume',
            type: 'number',
            desc: 'kg / m³, summed over the items',
          },
          {
            name: 'itemCount / totalKoli',
            type: 'number',
            desc: 'Items and physical parcels',
          },
          {
            name: 'items[]',
            type: 'object[]',
            children: [
              { name: 'id', type: 'uuid' },
              { name: 'name', type: 'string', desc: 'As sent at create' },
              { name: 'invoice', type: 'string' },
              { name: 'description', type: 'string | null' },
              { name: 'quantity', type: 'integer' },
              { name: 'koli', type: 'integer' },
              { name: 'weight', type: 'number', desc: 'kg' },
              { name: 'volume', type: 'number', desc: 'm³' },
              { name: 'status', type: 'enum', desc: 'The item status' },
              {
                name: 'return',
                type: 'object | null',
                desc: 'When the item was returned on the spot',
                children: [
                  { name: 'reasonCode', type: 'string' },
                  { name: 'reason', type: 'string | null' },
                  { name: 'reasonNote', type: 'string | null' },
                  { name: 'weight', type: 'number | null', desc: 'kg' },
                ],
              },
            ],
          },
          {
            name: 'media[]',
            type: 'object[]',
            desc: 'Every proof photo, once each, oldest first — the same objects as event.media',
            children: MEDIA_FIELDS,
          },
          {
            name: 'timeline[]',
            type: 'object[]',
            desc: 'Webhook events, newest first; no sequence',
            children: [
              { name: 'id', type: 'uuid' },
              {
                name: 'type',
                type: 'enum',
                desc: 'STATUS_CHANGED',
              },
              { name: 'status', type: 'enum', desc: 'The shipment status' },
              { name: 'message', type: 'string' },
              { name: 'hubName', type: 'string | null' },
              {
                name: 'reason',
                type: 'object | null',
                desc: 'On FAILED and CANCELLED',
                children: REASON_FIELDS,
              },
              { name: 'media[]', type: 'object[]', children: MEDIA_FIELDS },
              {
                name: 'createdBy',
                type: 'string | null',
                desc: 'Ops, rider ("{code} - {name}"), your user, or system',
              },
              { name: 'occurredAt', type: 'ISO-8601 UTC' },
            ],
          },
        ]}
      />
      <p>
        Shipment lifecycle:{' '}
        <Code>CREATED → AT_HUB → IN_DELIVERY → COMPLETED</Code>, with{' '}
        <Code>IN_DISPUTE</Code> while a return is in progress and terminals{' '}
        <Code>RETURNED</Code> / <Code>CANCELLED</Code> / <Code>FAILED</Code>. A{' '}
        <Code>COMPLETED</Code> shipment with <Code>hasReturn: true</Code> had at
        least one item returned on the spot. Treat unknown statuses as
        forward-compatible.
      </p>
      <ErrorList
        errors={[NOT_FOUND, OTHER_CLIENT, scopeError('shipments:read')]}
      />
    </Section>

    <Section
      hidden={!access.shipments}
      id="cancel-shipment"
      icon="ri-close-circle-line"
      title="Cancel shipment"
      code={
        <>
          <CodeBlock label="Request">
            {curl('POST', `/v1/integration/shipments/${ID_VAR}/cancel`, {
              body: `'{ "reason": "Customer cancelled the order" }'`,
            })}
          </CodeBlock>
          <CodeBlock label="Response 200 (condensed)">{`{
  "status": "Success",
  "data": {
    "id": "${SHIPMENT_ID}",
    "waybill": "DLCTH260914AB12CD3",
    "bookingId": "PO-2026-000123",
    "status": "CANCELLED",
    "cancelledAt": "2026-09-14T02:10:00.000Z",
    "cancellationReason": "Customer cancelled the order",
    "itemCount": 1,
    "items": [ { "id": "d1e2f3a4-…", "status": "CANCELLED", … } ]
  }
}`}</CodeBlock>
        </>
      }
    >
      <Method verb="POST" path="/v1/integration/shipments/:id/cancel" />
      <p>
        Cancels the shipment and all its items.{' '}
        <b className="text-text-strong-950">
          Only <Code>CREATED</Code> shipments can be cancelled
        </b>{' '}
        — once goods are scanned at a hub, cancellation becomes an ops process
        (contact Dash).
      </p>
      <ParamList
        title="Request body"
        params={[
          {
            name: 'reason',
            type: 'string',
            desc: 'Why it was cancelled, up to 1000 characters.',
          },
        ]}
      />
      <ErrorList errors={CANCEL_ERRORS} />
    </Section>

    <Section
      hidden={!access.orders}
      id="create-outbound-order"
      icon="ri-archive-stack-line"
      title="Create outbound order"
      code={
        <>
          <CodeBlock label="Request">
            {curl('POST', '/v1/integration/outbound-orders', {
              body: '@order.json',
            })}
          </CodeBlock>
          <CodeBlock label="Body — order.json">
            {annotate(json(ORDER_REQUEST), ORDER_REQUEST_NOTES)}
          </CodeBlock>
          <CodeBlock label="Response 200">
            {annotate(json(ORDER_CREATE_RESPONSE), [
              ['"result": "CREATED"', '"EXISTS" on an idempotent replay'],
              ['"hub": {', 'the hub Dash chose'],
              ['"fulfillment": null', 'set once it ships'],
            ])}
          </CodeBlock>
        </>
      }
    >
      <Method verb="POST" path="/v1/integration/outbound-orders" />
      <p>
        For goods that already sit in a Dash warehouse: send the order, and Dash
        picks the hub, picks, packs and ships it.{' '}
        <b className="text-text-strong-950">
          Idempotent by <Code>externalID</Code>
        </b>{' '}
        — replaying it returns <Code>{'"result": "EXISTS"'}</Code> with the
        order you already created, so retrying on timeout is safe.
      </p>
      <p>
        <b className="text-text-strong-950">You don&apos;t pick the hub.</b>{' '}
        Dash places the order at the hub that holds enough stock for{' '}
        <i>every</i> line — one order ships from one hub — and, when several do,
        the one nearest to the destination. No hub holds enough →{' '}
        <Code>422</Code>, nothing is created. Stock is checked when you create,
        and reserved when the warehouse starts picking.
      </p>
      <p>
        Once the order ships, it becomes a regular shipment: its waybill is in{' '}
        <Code>fulfillment.waybill</Code>, and the shipment sends your{' '}
        <a
          href="#webhooks"
          className="font-medium text-primary underline-offset-2 hover:underline"
        >
          shipment webhooks
        </a>{' '}
        with <Code>bookingId</Code> = your <Code>externalID</Code>.{' '}
        <b className="text-text-strong-950">
          Order status webhooks are not available yet
        </b>{' '}
        — read the order with <Code>GET</Code> to follow it until it ships.
      </p>
      <p>
        <b className="text-text-strong-950">Sandbox</b> (a{' '}
        <Code>dash_test_…</Code> key): orders go to{' '}
        <Code>Dash Sandbox Hub</Code>, and every SKU of your catalog holds{' '}
        <Code>100</Code> — up to 100 per SKU is created, more is a{' '}
        <Code>422</Code>. Sandbox orders are never processed by a warehouse and
        stay <Code>OPEN</Code> until you cancel them.
      </p>
      <ParamList
        title="Request body"
        params={[
          {
            name: 'externalID',
            type: 'string',
            required: true,
            desc: 'Your order id, up to 64 characters — the idempotency key (per environment).',
          },
          {
            name: 'expectedDeliveryDate',
            type: 'string',
            required: true,
            desc: (
              <>
                <Code>YYYY-MM-DD</Code>, not before today (Jakarta time).
              </>
            ),
          },
          {
            name: 'recipient',
            type: 'object',
            required: true,
            children: [
              { name: 'name', type: 'string', required: true },
              {
                name: 'phone',
                type: 'string',
                required: true,
                desc: 'e.g. 081234567890 (spaces and dashes are removed)',
              },
            ],
          },
          {
            name: 'destination',
            type: 'object',
            required: true,
            children: [
              {
                name: 'address',
                type: 'string',
                required: true,
                desc: 'Full delivery address.',
              },
              {
                name: 'latitude',
                type: 'number',
                required: true,
                desc: 'Inside Indonesia — the address is not geocoded.',
              },
              { name: 'longitude', type: 'number', required: true },
              {
                name: 'notes',
                type: 'string',
                desc: 'For the courier (gate colour, floor …).',
              },
            ],
          },
          {
            name: 'lines[]',
            type: 'array',
            required: true,
            desc: '1 to 200 lines, each SKU once.',
            children: [
              {
                name: 'sku',
                type: 'string',
                required: true,
                desc: 'A SKU of your catalog at Dash (active).',
              },
              {
                name: 'qty',
                type: 'number',
                required: true,
                desc: 'More than 0, up to 2 decimals.',
              },
            ],
          },
          { name: 'notes', type: 'string', desc: 'For the warehouse.' },
        ]}
      />
      <ErrorList errors={ORDER_CREATE_ERRORS} />
    </Section>

    <Section
      hidden={!access.orders}
      id="track-outbound-order"
      icon="ri-radar-line"
      title="Get & list outbound orders"
      code={
        <>
          <CodeBlock label="Request — one order">
            {curl('GET', `/v1/integration/outbound-orders/${ORDER_ID_VAR}`)}
          </CodeBlock>
          <CodeBlock label="Request — list">
            {curl(
              'GET',
              '/v1/integration/outbound-orders?status=OPEN&createdFrom=2026-10-01&page=1&limit=20'
            )}
          </CodeBlock>
          <CodeBlock label="Response 200 — list (one order out for delivery)">
            {annotate(
              json({
                status: 'Success',
                data: {
                  data: [
                    {
                      ...ORDER,
                      status: 'IN_DELIVERY',
                      fulfillment: {
                        carrier: 'DASH',
                        status: 'IN_DELIVERY',
                        waybill: 'DLATL2610231QPR5UD',
                        shipmentID: SHIPMENT_ID,
                      },
                      lines: ORDER.lines.slice(0, 1),
                    },
                  ],
                  pagination: { total: 1, page: 1, size: 20, lastPage: 1 },
                },
              }),
              [
                ['"carrier": "DASH"', 'LION_PARCEL · HANDOVER'],
                ['"shipmentID"', 'track it with the shipment API'],
              ]
            )}
          </CodeBlock>
        </>
      }
    >
      <Method verb="GET" path="/v1/integration/outbound-orders/:id" />
      <Method verb="GET" path="/v1/integration/outbound-orders" />
      <p>
        One order, or your orders newest first — every order of your company in
        the key&apos;s environment, including the ones Dash ops created for you.
        Same object as the create response.
      </p>
      <ParamList
        title="List query"
        params={[
          {
            name: 'status',
            type: 'enum',
            desc: (
              <>
                <Code>OPEN</Code> · <Code>PREPARING</Code> ·{' '}
                <Code>READY_TO_PICKUP</Code> · <Code>SHIPPED</Code> ·{' '}
                <Code>IN_DELIVERY</Code> · <Code>DELIVERED</Code> ·{' '}
                <Code>FAILED</Code> · <Code>CANCELLED</Code>
              </>
            ),
          },
          {
            name: 'search',
            type: 'string',
            desc: 'Order code, externalID, recipient or waybill (contains).',
          },
          {
            name: 'createdFrom / createdTo',
            type: 'string',
            desc: (
              <>
                <Code>YYYY-MM-DD</Code>, Jakarta time, inclusive.
              </>
            ),
          },
          { name: 'page', type: 'integer', desc: 'From 1 (default 1).' },
          { name: 'limit', type: 'integer', desc: 'Up to 100 (default 20).' },
        ]}
      />
      <ParamList
        title="Order fields"
        params={[
          { name: 'id', type: 'string', desc: 'Dash order id (UUID).' },
          { name: 'code', type: 'string', desc: 'Dash order number.' },
          { name: 'externalID', type: 'string' },
          {
            name: 'status',
            type: 'enum',
            desc: (
              <>
                <Code>OPEN</Code> → <Code>PREPARING</Code> (picking) →{' '}
                <Code>READY_TO_PICKUP</Code> → <Code>SHIPPED</Code> →{' '}
                <Code>IN_DELIVERY</Code> → <Code>DELIVERED</Code>, or{' '}
                <Code>FAILED</Code> / <Code>CANCELLED</Code>.
              </>
            ),
          },
          {
            name: 'hub',
            type: 'object',
            desc: 'The hub Dash chose.',
            children: [
              { name: 'id', type: 'integer' },
              { name: 'name', type: 'string' },
              { name: 'code', type: 'string | null' },
            ],
          },
          {
            name: 'orderDate / expectedDeliveryDate',
            type: 'string',
            desc: <Code>YYYY-MM-DD</Code>,
          },
          {
            name: 'recipient',
            type: 'object',
            children: [
              { name: 'name', type: 'string' },
              { name: 'phone', type: 'string' },
            ],
          },
          {
            name: 'destination',
            type: 'object',
            desc: 'address, latitude, longitude, notes — as sent; plus cityID / city: the destination city as Dash resolved it (its city master, from the address, else the point) — both null while unresolved, re-read on GET.',
          },
          {
            name: 'lines[]',
            type: 'array',
            children: [
              { name: 'sku', type: 'string' },
              { name: 'itemName', type: 'string' },
              { name: 'uomName', type: 'string' },
              { name: 'qty', type: 'number' },
            ],
          },
          { name: 'notes', type: 'string' },
          {
            name: 'fulfillment',
            type: 'object | null',
            desc: 'null until the order ships.',
            children: [
              {
                name: 'carrier',
                type: 'enum',
                desc: (
                  <>
                    <Code>DASH</Code> · <Code>LION_PARCEL</Code> ·{' '}
                    <Code>HANDOVER</Code> (handed over at the hub, no waybill)
                  </>
                ),
              },
              { name: 'status', type: 'string', desc: 'The delivery status.' },
              { name: 'waybill', type: 'string | null' },
              {
                name: 'shipmentID',
                type: 'string | null',
                desc: 'DASH only — the shipment, readable with Get shipment.',
              },
            ],
          },
          {
            name: 'cancellation',
            type: 'object | null',
            desc: 'reason, cancelledAt — when CANCELLED.',
          },
          { name: 'createdAt / updatedAt', type: 'string', desc: 'ISO 8601.' },
        ]}
      />
      <ErrorList
        errors={[
          ORDER_NOT_FOUND,
          OTHER_CLIENT_ORDER,
          scopeError('shipments:read'),
        ]}
      />
    </Section>

    <Section
      hidden={!access.orders}
      id="cancel-outbound-order"
      icon="ri-close-circle-line"
      title="Cancel outbound order"
      code={
        <CodeBlock label="Request">
          {curl(
            'POST',
            `/v1/integration/outbound-orders/${ORDER_ID_VAR}/cancel`,
            {
              body: `'{ "reason": "Customer cancelled the order" }'`,
            }
          )}
        </CodeBlock>
      }
    >
      <Method verb="POST" path="/v1/integration/outbound-orders/:id/cancel" />
      <p>
        Cancels the order and releases any stock reserved for it.{' '}
        <b className="text-text-strong-950">Only before it ships</b> (
        <Code>OPEN</Code>, <Code>PREPARING</Code>, <Code>READY_TO_PICKUP</Code>)
        — after that, cancel the shipment. Answers the order,{' '}
        <Code>CANCELLED</Code>.
      </p>
      <ParamList
        title="Request body"
        params={[
          {
            name: 'reason',
            type: 'string',
            desc: 'Why, up to 500 characters. Optional.',
          },
        ]}
      />
      <ErrorList errors={ORDER_CANCEL_ERRORS} />
    </Section>

    <Section
      hidden={!access.orders}
      id="get-stock"
      icon="ri-stack-line"
      title="Stock at Dash"
      code={
        <>
          <CodeBlock label="Request">
            {curl('GET', '/v1/integration/stocks?search=mask&page=1&limit=50')}
          </CodeBlock>
          <CodeBlock label="Response 200">
            {annotate(
              json({
                status: 'Success',
                data: {
                  data: [
                    {
                      sku: '100002',
                      itemName: 'Atlas Mask',
                      uomName: 'pcs',
                      qtyOnHand: 120,
                      qtyReserved: 20,
                      qtyAvailable: 95,
                      batches: [
                        {
                          batchNumber: 'B2026-03',
                          expiredDate: '2027-03-31',
                          qtyOnHand: 100,
                          qtyReserved: 20,
                          qtyAvailable: 80,
                        },
                        {
                          batchNumber: 'B2026-05',
                          expiredDate: '2027-05-31',
                          qtyOnHand: 20,
                          qtyReserved: 0,
                          qtyAvailable: 15,
                        },
                      ],
                      updatedAt: '2026-10-29T03:15:02.000Z',
                    },
                  ],
                  pagination: { total: 1, page: 1, size: 50, lastPage: 1 },
                },
              }),
              [
                ['"qtyAvailable": 95', 'what a new order can use now'],
                ['"batches"', 'earliest expiry first'],
              ]
            )}
          </CodeBlock>
        </>
      }
    >
      <Method verb="GET" path="/v1/integration/stocks" />
      <p>
        Your stock at Dash&apos;s warehouses, one row per SKU (every hub summed
        — which hub holds it is Dash&apos;s business), sorted by SKU. Only SKUs
        with stock on hand are listed. Read-only; nothing here reserves stock.
        At most <b className="text-text-strong-950">100 rows per call</b> — page
        through <Code>pagination.lastPage</Code>. In Sandbox every active SKU of
        your catalog holds 100 units.
      </p>
      <ParamList
        title="Query"
        params={[
          {
            name: 'search',
            type: 'string',
            desc: 'SKU code or item name, contains (case-insensitive). Optional.',
          },
          { name: 'page', type: 'integer', desc: 'Default 1.' },
          {
            name: 'limit',
            type: 'integer',
            desc: 'Default 20, at most 100 — above that is a 400.',
          },
        ]}
      />
      <ParamList
        title="Stock row"
        params={[
          { name: 'sku', type: 'string' },
          { name: 'itemName', type: 'string' },
          { name: 'uomName', type: 'string', desc: 'Unit of every quantity.' },
          {
            name: 'qtyOnHand',
            type: 'number',
            desc: 'All stock, quarantine and inactive storages included.',
          },
          {
            name: 'qtyReserved',
            type: 'number',
            desc: 'Held by picking orders that have not shipped yet.',
          },
          {
            name: 'qtyAvailable',
            type: 'number',
            desc: 'What a new order can use now: allocatable stock minus reservations (the rule Dash uses to pick a hub for your orders). Can be less than on hand − reserved.',
          },
          {
            name: 'batches[]',
            type: 'array',
            desc: 'The same three quantities per batch, earliest expiry first (no expiry last).',
            children: [
              { name: 'batchNumber', type: 'string' },
              { name: 'expiredDate', type: 'YYYY-MM-DD | null' },
              { name: 'qtyOnHand', type: 'number' },
              { name: 'qtyReserved', type: 'number' },
              { name: 'qtyAvailable', type: 'number' },
            ],
          },
          {
            name: 'updatedAt',
            type: 'datetime | null',
            desc: 'Last stock movement on this SKU; null in Sandbox.',
          },
        ]}
      />
    </Section>

    <Section
      hidden={!access.shipments}
      id="webhooks"
      icon="ri-broadcast-line"
      title="Webhooks"
      code={
        <>
          <CodeBlock label="Request">{`curl -X POST ${BASE_URL}/v1/integration/webhook-endpoints \\
  -H "Authorization: Bearer <portal JWT>" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(REGISTER_REQUEST)}'`}</CodeBlock>
          <CodeBlock label="Response 200">
            {annotate(json(REGISTER_RESPONSE), [
              ['"secret"', 'shown once, never again'],
            ])}
          </CodeBlock>
          <CodeBlock label="Event delivery (what Dash POSTs to your URL)">{`POST ${WEBHOOK_URL}
x-dash-signature: t=1789800909,v1=5f1d…

${annotate(json(COMPLETED_EVENT), [
  ['"status": "Success"', 'always Success / OK'],
  ['"event": {', 'one timeline entry'],
  ['"message": "Pengantaran', 'readable text per status'],
  ['"hubName"', 'AT_HUB / DISPATCHED only'],
  ['"reason"', 'on FAILED / CANCELLED'],
  ['"media": [', 'the photos of this event only'],
  ['"shipment": {', 'as of this event'],
  ['"environment"', 'LIVE | TEST'],
  ['"courier": {', 'null until assigned'],
  ['"uuid": "6f3c', 'stable id; nullable'],
  ['"totalWeight"', 'kg'],
  ['"items": [', 'item status + return info'],
])}

→ respond 2xx quickly; anything else is retried with backoff`}</CodeBlock>
          <CodeBlock label="AT_HUB (only the fields that differ)">
            {annotate(json(AT_HUB_EVENT), [
              ['"hubName"', 'the hub that holds the goods'],
            ])}
          </CodeBlock>
          <CodeBlock label="FAILED (only the fields that differ)">
            {annotate(json(FAILED_EVENT), [
              ['"message"', "the reason's text"],
              ['"reason": {', 'why this attempt failed'],
              ['"type": "FAILED_PROOF"', "the failed attempt's photo"],
              ['"items": [', 'FAILED retries; LOST is final'],
            ])}
          </CodeBlock>
        </>
      }
    >
      <Method verb="POST" path="/v1/integration/webhook-endpoints" />
      <p>
        Register an HTTPS endpoint and Dash pushes every{' '}
        <b className="text-text-strong-950">shipment event</b> to it instead of
        you polling — there is no event subscription, an endpoint receives them
        all; ignore any value you don&apos;t handle. Endpoints follow the same
        environment wall as keys — a <Code>TEST</Code> endpoint receives only
        sandbox events, a <Code>LIVE</Code> endpoint only production events.
        Registration is a portal (sign-in) operation, not an API-key one: a key
        can never point deliveries somewhere new.
      </p>
      <ParamList
        title="Request body"
        params={[
          {
            name: 'url',
            type: 'string',
            required: true,
            desc: 'https:// only — plain http is rejected',
          },
          {
            name: 'environment',
            type: 'string',
            required: true,
            desc: 'LIVE | TEST',
          },
        ]}
      />
      <p>
        The <Code>secret</Code> is shown once. Every delivery is signed with it:
        header <Code>x-dash-signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</Code>{' '}
        where{' '}
        <Code>
          v1 = HMAC_SHA256(secret, `$&#123;t&#125;.$&#123;rawBody&#125;`)
        </Code>
        . Compare in constant time and reject timestamps older than 5 minutes.
        Retried deliveries carry the{' '}
        <b className="text-text-strong-950">same</b> <Code>event.id</Code> —
        dedupe on it, and apply events by <Code>event.sequence</Code>, never by
        arrival order. A failed delivery does not hold back the shipment&apos;s
        later events: they are still sent, and the failed one arrives late on
        its retry — so a lower <Code>sequence</Code> can arrive after a higher
        one.
      </p>

      <h3 className="pt-2 text-base font-semibold text-text-strong-950">
        Payload
      </h3>
      <p>
        Every delivery has the same envelope as Dash&apos;s API responses:{' '}
        <Code>
          {'{ status: "Success", message: "OK", data: { event, shipment } }'}
        </Code>
        . <Code>event</Code> is the latest thing that happened — one entry of
        the shipment&apos;s timeline — with a readable message, the hub, the
        reason and its photos; <Code>shipment</Code> is the shipment as it stood
        right after it.{' '}
        <b className="text-text-strong-950">
          Both are frozen when the event happens
        </b>
        , so a retry hours later still shows that moment. Per shipment, apply an
        event only when <Code>event.sequence</Code> is higher than the last one
        you applied. The full history is on{' '}
        <Code>GET /v1/integration/shipments/:id</Code> (<Code>timeline[]</Code>
        ). Ignore fields and values you don&apos;t know — new ones are added
        without notice.
      </p>
      <ParamList
        title="Payload fields"
        params={[
          {
            name: 'event.id',
            type: 'uuid',
            desc: 'Dedupe key — retries carry the same value',
          },
          {
            name: 'event.type',
            type: 'enum',
            desc: 'STATUS_CHANGED (table below)',
          },
          {
            name: 'event.sequence',
            type: 'integer',
            desc: 'Per shipment. Order by this; gaps are legal',
          },
          {
            name: 'event.status',
            type: 'enum',
            desc: 'CREATED | AT_HUB | PARTIALLY_DISPATCHED | DISPATCHED | IN_DELIVERY | IN_DISPUTE | COMPLETED | RETURNED | CANCELLED | FAILED — the shipment status after the event',
          },
          {
            name: 'event.message',
            type: 'string',
            desc: 'Readable text per status (table below); FAILED says why',
          },
          {
            name: 'event.hubName',
            type: 'string | null',
            desc: 'Only on AT_HUB, PARTIALLY_DISPATCHED and DISPATCHED',
          },
          {
            name: 'event.occurredAt',
            type: 'ISO-8601 UTC',
            desc: 'Informational — do not order by it',
          },
          {
            name: 'event.reason',
            type: 'object | null',
            desc: 'On FAILED and CANCELLED; either field can be null. For a reason code with no standard text (the courier picked "other"), message is what the courier typed',
            children: REASON_FIELDS,
          },
          {
            name: 'event.media[]',
            type: 'array',
            desc: 'The photos of this event only; [] when none. A photo rides the event it came with: pickup on IN_DELIVERY, proof of delivery on COMPLETED, the failed attempt on FAILED',
            children: MEDIA_FIELDS,
          },
          {
            name: 'shipment.environment',
            type: 'LIVE | TEST',
            desc: 'Always present. TEST for a sandbox shipment (created with a dash_test_ key), LIVE otherwise — check it if your Sandbox and Production endpoints share one URL',
          },
          {
            name: 'shipment.courier',
            type: 'object | null',
            desc: 'The courier of the most recent leg. Null until a courier is assigned',
            children: COURIER_FIELDS,
          },
          {
            name: 'shipment.sender',
            type: 'object',
            children: CONTACT_OUT_FIELDS,
          },
          {
            name: 'shipment.recipient',
            type: 'object',
            children: CONTACT_OUT_FIELDS,
          },
          {
            name: 'shipment.origin',
            type: 'object | null',
            desc: 'Null for a DOCKING + DELIVERY sent without origin, until the hub scan',
            children: LOCATION_OUT_FIELDS,
          },
          {
            name: 'shipment.destination',
            type: 'object',
            children: LOCATION_OUT_FIELDS,
          },
          {
            name: 'shipment.items[]',
            type: 'array',
            children: [
              { name: 'id', type: 'uuid' },
              { name: 'name', type: 'string' },
              { name: 'weight', type: 'number', desc: 'kg' },
              {
                name: 'status',
                type: 'enum',
                desc: 'CREATED | HUB_SCANNED | DISPATCHED | HANDOVER_SCANNED | IN_DELIVERY | FAILED | COMPLETED | IN_RETURN | RETURNED_TO_HUB | RETURNED_TO_CLIENT | RETURNED | CANCELLED | LOST',
              },
              {
                name: 'return',
                type: 'object | null',
                desc: 'Once the item is flagged for return',
                children: [
                  { name: 'reasonCode', type: 'string' },
                  { name: 'reason', type: 'string | null' },
                  { name: 'reasonNote', type: 'string | null' },
                ],
              },
            ],
          },
        ]}
      />
      <p className="text-[13px]">
        The rest of <Code>shipment</Code> mirrors what you sent on create:{' '}
        <Code>id</Code>, <Code>waybill</Code>, <Code>bookingId</Code>,{' '}
        <Code>serviceType</Code>, plus <Code>totalWeight</Code> (kg) and the{' '}
        <Code>createdAt</Code> / <Code>dispatchedAt</Code> timestamps. Statuses
        use the same names as the detail endpoint.
      </p>

      <h3 className="pt-2 text-base font-semibold text-text-strong-950">
        Events
      </h3>
      <p>
        A status change arrives as <Code>event.type</Code>{' '}
        <Code>STATUS_CHANGED</Code> with the new <Code>event.status</Code>.
      </p>
      <FieldTable
        columns={['Event', 'event.message', 'Sent when']}
        rows={SHIPMENT_WEBHOOK_EVENTS.map((e) => [e.status, e.message, e.when])}
      />
      <p className="text-xs">
        <Code>{'{hubName}'}</Code> / <Code>{'{riderName}'}</Code> are the hub
        the goods arrived at and the rider just assigned; when Dash doesn&apos;t
        know one, the message reads without it.
      </p>
      <p>
        <b className="text-text-strong-950">
          <Code>FAILED</Code> is not always final.
        </b>{' '}
        <Code>event.reason</Code> says why. Items in <Code>FAILED</Code> will be
        retried — expect <Code>IN_DELIVERY</Code> again; items in{' '}
        <Code>LOST</Code> are final. If the reason changes while the shipment
        stays <Code>FAILED</Code>, <Code>FAILED</Code> is sent again. When the
        courier picked &quot;other&quot; and typed the reason (a code with no
        standard text, e.g. <Code>EC4099</Code>), <Code>reason.message</Code> is
        what they typed and <Code>event.message</Code> stays{' '}
        <Code>Pengantaran gagal</Code>.
      </p>
      <ErrorList
        title="Registration errors"
        errors={[
          {
            status: 400,
            when: 'You already have an active endpoint in that environment — one per environment; disable it first',
            error: `You already have an active Sandbox (TEST) endpoint (${WEBHOOK_URL}). Only one active endpoint per environment is allowed — disable it first, then add the new one.`,
          },
          {
            status: 400,
            when: 'This URL is already your active endpoint',
            error:
              'This URL is already your active Sandbox (TEST) endpoint — nothing to add.',
          },
          {
            status: 400,
            when: 'url is not https://',
            error: 'url must be a URL address',
          },
        ]}
      />
    </Section>
  </div>
);

export default ApiDocsTab;
